import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Typography, styled, Tooltip, Button, Box } from '@mui/material';

import { Header } from 'components/Header';
import { Footer } from 'components/Footer';

import { useRootStore } from 'stores/RootStore';
import AmplitudeService from 'services/amplitude.service';

import { UnderwritingDashboard } from './UnderwritingDashboard';
import { CreateNewUnderwritingModal } from './CreateNewUnderwritingModal';
import { GooglePlaceLocation } from '../types';

const PageHeader = styled('div')(({ theme }) => ({
  background: 'rgba(250, 250, 250, 1)',
  padding: '24px 24px',
}));

const PageBody = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  background: 'rgba(250, 250, 250, 1)',
}));

const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}));

const PageHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const UnderwritingDashboardPage: React.FC = observer(() => {
  const { underwritingDashboardPageStore: pageStore, routeStore, authStore } = useRootStore();
  const { currentUser } = authStore;
  const { loadStatus, message } = pageStore;
  const [showCreateNewUnderwritingModal, setShowCreateNewUnderwritingModal] = useState(false);

  useEffect(() => {
    if (loadStatus === 'pending') {
      pageStore.load();
    }
  }, [loadStatus, pageStore]);

  const handleNewClick = useCallback(() => {
    setShowCreateNewUnderwritingModal(true);
  }, []);

  const createNewUnderwriting = useCallback(
    async (input: {
      location: GooglePlaceLocation;
      approaches: string[];
      incomeType: string;
      valueConclusions: string[];
    }) => {
      setShowCreateNewUnderwritingModal(false);
      try {
        const newUnderwriting = await pageStore.create(input);
        if (!newUnderwriting) {
          alert('Failed to create');
          return;
        }
        AmplitudeService.trackEvent('Create Underwriting Property', {
          id: newUnderwriting.id,
          address: newUnderwriting.streetAddress,
          user: currentUser,
        });
        routeStore.navigateByName('propertySummary', { id: newUnderwriting.id });
      } catch (error) {
        alert((error as Error).message);
      }
    },
    [pageStore, currentUser, routeStore]
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="jobs-container">
      <Header />
      <PageHeader>
        <PageHeaderRow>
          <Typography variant="h6" pl={1}>
            Properties
          </Typography>
          <Toolbar>
            <Tooltip title="Create a new underwriting item">
              <Button variant="contained" color="primary" onClick={handleNewClick}>
                + New Property
              </Button>
            </Tooltip>
          </Toolbar>
        </PageHeaderRow>
      </PageHeader>
      <PageBody>
        {loadStatus === 'loading' && <div>Loading...</div>}
        {loadStatus === 'error' && <div>Error: {message}</div>}
        {loadStatus === 'loaded' && <UnderwritingDashboard properties={pageStore.properties || []} />}
      </PageBody>
      <Footer />
      {showCreateNewUnderwritingModal && (
        <CreateNewUnderwritingModal
          open={showCreateNewUnderwritingModal}
          onClose={() => setShowCreateNewUnderwritingModal(false)}
          onCreate={createNewUnderwriting}
        />
      )}
    </Box>
  );
});

export { UnderwritingDashboardPage };
